/**
 * DevicesView.js — Portal Devices tab (owner only)
 *
 * Subtabs:
 *   Devices        — Live list of registered POS tablets (Firestore snapshot).
 *                    Shows presence, station assignment, friendly name, deactivate.
 *   Add New Device — TinyMDM enrollment flow: generate a QR code / link to
 *                    enroll a new Android device into kiosk mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { assignReaderToDevice, clearReaderAssignment } from '../../utilities/readerAssignment';

const functions = getFunctions();
const createDeviceEnrollment   = httpsCallable(functions, 'createDeviceEnrollment');
const listDeviceEnrollments    = httpsCallable(functions, 'listDeviceEnrollments');
const deleteDeviceEnrollmentFn = httpsCallable(functions, 'deleteDeviceEnrollment');

// ── Shared helpers ────────────────────────────────────────────────────────────

const STATION_OPTIONS = [
  { value: null,            label: 'None (Role Select)' },
  { value: 'host',          label: 'Host / Dashboard'   },
  { value: 'kitchen',       label: 'Kitchen Display'    },
  { value: 'waitlistKiosk', label: 'Waitlist Kiosk'     },
];

const STATION_LABELS = {
  host:          'Host / Dashboard',
  kitchen:       'Kitchen Display',
  waitlistKiosk: 'Waitlist Kiosk',
  bar:           'Bar Display',
};

const getPresenceStatus = (lastSeenAt) => {
  if (!lastSeenAt) return 'unknown';
  const diff = Date.now() - new Date(lastSeenAt).getTime();
  if (diff < 10 * 60 * 1000) return 'online';
  if (diff < 60 * 60 * 1000) return 'recent';
  return 'offline';
};

const relativeTime = (iso) => {
  if (!iso) return 'Never';
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  if (mins < 1)   return 'Just now';
  if (mins < 60)  return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const PRESENCE_STYLES = {
  online:  { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Online'          },
  recent:  { dot: 'bg-amber-400',   text: 'text-amber-600',   label: 'Recently active' },
  offline: { dot: 'bg-neutral-300', text: 'text-neutral-400', label: 'Offline'         },
  unknown: { dot: 'bg-neutral-200', text: 'text-neutral-300', label: 'Unknown'         },
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-neutral-200 rounded-xl p-4 ${className}`}>
    {children}
  </div>
);

// ── Subtab 1: Registered Devices ─────────────────────────────────────────────

const RegisteredDevicesTab = ({ restaurantId }) => {
  const [devices,       setDevices]       = useState([]);
  const [readers,       setReaders]       = useState([]); // known readers from POS connections
  const [loading,       setLoading]       = useState(true);
  const [savingId,      setSavingId]      = useState(null);
  const [editingName,   setEditingName]   = useState(null);
  const [nameInput,     setNameInput]     = useState('');
  const [assigningId,   setAssigningId]   = useState(null); // deviceId whose picker is open
  const [assigningSaving, setAssigningSaving] = useState(null); // deviceId being saved
  const [, forceRender] = useState(0);

  useEffect(() => {
    const t = setInterval(() => forceRender(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    const unsub = onSnapshot(
      collection(doc(db, 'restaurants_data', restaurantId), 'devices'),
      (snap) => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0));
        setDevices(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [restaurantId]);

  // Live subscription to known readers (registered by tablets when they connect)
  useEffect(() => {
    if (!restaurantId) return;
    const unsub = onSnapshot(
      collection(doc(db, 'restaurants_data', restaurantId), 'readers'),
      (snap) => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.lastSeenAt || '').localeCompare(a.lastSeenAt || ''));
        setReaders(list);
      },
      () => {},
    );
    return () => unsub();
  }, [restaurantId]);

  // TinyMDM auto-names tablets "Android device" — treat that as unset
  const isTinyMdmDefault = (name) => !name || /^android\s+device$/i.test(name.trim());
  const displayName = (name) => isTinyMdmDefault(name) ? 'Enter Device Name' : name;

  const save = async (deviceId, patch) => {
    setSavingId(deviceId);
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId, 'devices', deviceId), patch);
    } catch (e) {
      console.error('[DevicesView] save failed:', e);
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveName = async (deviceId) => {
    const trimmed = nameInput.trim();
    if (trimmed) await save(deviceId, { friendlyName: trimmed });
    setEditingName(null);
    setNameInput('');
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading devices…</p>;

  if (devices.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-2xl mb-2">📱</p>
          <p className="text-sm font-medium text-neutral-600 mb-1">No devices registered yet</p>
          <p className="text-sm text-neutral-400">
            Devices appear here automatically when the GoHost POS app is opened on a tablet.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-400">
        {devices.length} device{devices.length !== 1 ? 's' : ''} registered.
        Online status updates within 10 minutes of last activity.
      </p>

      {devices.map(device => {
        const presence = getPresenceStatus(device.lastSeenAt);
        const ps       = PRESENCE_STYLES[presence];
        const isSaving  = savingId === device.id;
        const isEditing = editingName === device.id;
        const currentStationLabel = device.currentStation
          ? (STATION_LABELS[device.currentStation] || device.currentStation)
          : null;

        return (
          <Card key={device.id} className={device.isActive === false ? 'opacity-50' : ''}>
            {/* Top row: name + presence + deactivate */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="relative shrink-0 mt-0.5">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${ps.dot}`} />
                  {presence === 'online' && (
                    <span className={`absolute inset-0 rounded-full ${ps.dot} animate-ping opacity-60`} />
                  )}
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSaveName(device.id);
                        if (e.key === 'Escape') { setEditingName(null); setNameInput(''); }
                      }}
                      className="px-2 py-1 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 w-44"
                    />
                    <button
                      onClick={() => handleSaveName(device.id)}
                      disabled={isSaving}
                      className="px-2.5 py-1 text-xs font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                    >
                      {isSaving ? '…' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setEditingName(null); setNameInput(''); }}
                      className="text-xs text-neutral-400 hover:text-neutral-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingName(device.id); setNameInput(isTinyMdmDefault(device.friendlyName) ? '' : (device.friendlyName || '')); }}
                    className={`text-sm font-semibold hover:text-brand-blue truncate text-left group ${isTinyMdmDefault(device.friendlyName) ? 'text-neutral-400 italic' : 'text-neutral-800'}`}
                  >
                    {displayName(device.friendlyName)}
                    <span className="ml-1.5 text-neutral-300 group-hover:text-neutral-500 text-xs">✏</span>
                  </button>
                )}
              </div>

              <button
                onClick={() => save(device.id, { isActive: !device.isActive })}
                disabled={isSaving}
                className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                  device.isActive === false
                    ? 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
              >
                {device.isActive === false ? 'Reactivate' : 'Deactivate'}
              </button>
            </div>

            {/* Status row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-xs">
              <span className={`font-medium ${ps.text}`}>{ps.label}</span>
              <span className="text-neutral-400">Last seen: {relativeTime(device.lastSeenAt)}</span>
              {device.platform && <span className="text-neutral-400 capitalize">{device.platform}</span>}
              {device.appVersion && <span className="text-neutral-400">v{device.appVersion}</span>}
              {currentStationLabel && (
                <span className="inline-flex items-center gap-1 bg-brand-blue/10 text-brand-blue font-semibold px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-blue inline-block" />
                  Currently: {currentStationLabel}
                </span>
              )}
            </div>

            {/* Card Reader Assignment */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                Card Reader
              </p>

              {device.assignedReader ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200">
                    <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-green-800 truncate">
                        {device.assignedReader.label || device.assignedReader.serialNumber}
                      </p>
                      <p className="text-xs text-green-600 font-mono truncate">
                        {device.assignedReader.serialNumber}
                        {device.assignedReader.deviceType ? ` · ${device.assignedReader.deviceType}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAssigningId(assigningId === device.id ? null : device.id)}
                    className="shrink-0 text-xs font-semibold px-2.5 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 transition-colors"
                  >
                    Change
                  </button>
                  <button
                    disabled={assigningSaving === device.id}
                    onClick={async () => {
                      setAssigningSaving(device.id);
                      try { await clearReaderAssignment(restaurantId, device.id); }
                      catch (e) { console.error(e); }
                      finally { setAssigningSaving(null); setAssigningId(null); }
                    }}
                    className="shrink-0 text-xs font-semibold px-2.5 py-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {assigningSaving === device.id ? '…' : 'Unassign'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-xs text-neutral-400">
                    No reader assigned
                  </div>
                  <button
                    onClick={() => setAssigningId(assigningId === device.id ? null : device.id)}
                    className="shrink-0 text-xs font-semibold px-2.5 py-2 rounded-lg border border-[#5f799a] text-[#5f799a] hover:bg-[#5f799a]/10 transition-colors"
                  >
                    Assign
                  </button>
                </div>
              )}

              {/* Reader picker dropdown */}
              {assigningId === device.id && (
                <div className="mt-2 border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  {readers.length === 0 ? (
                    <p className="px-4 py-3 text-xs text-neutral-400">
                      No readers registered yet. Readers appear here when a POS tablet connects to one.
                    </p>
                  ) : (
                    readers.map(reader => {
                      const isCurrentlyAssignedHere = device.assignedReader?.serialNumber === reader.serialNumber;
                      const assignedElsewhere = !isCurrentlyAssignedHere &&
                        devices.find(d => d.id !== device.id && d.assignedReader?.serialNumber === reader.serialNumber);
                      return (
                        <button
                          key={reader.serialNumber}
                          disabled={isCurrentlyAssignedHere || assigningSaving === device.id}
                          onClick={async () => {
                            setAssigningSaving(device.id);
                            try {
                              await assignReaderToDevice(restaurantId, device.id, reader, devices);
                            } catch (e) { console.error(e); }
                            finally { setAssigningSaving(null); setAssigningId(null); }
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-neutral-100 last:border-0 transition-colors ${
                            isCurrentlyAssignedHere
                              ? 'bg-green-50 cursor-default'
                              : 'hover:bg-neutral-50 cursor-pointer'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-neutral-800 truncate">
                              {reader.label || reader.serialNumber}
                            </p>
                            <p className="text-xs text-neutral-400 font-mono truncate">
                              {reader.serialNumber}
                              {reader.deviceType ? ` · ${reader.deviceType}` : ''}
                            </p>
                            {assignedElsewhere && (
                              <p className="text-xs text-amber-600 font-medium mt-0.5">
                                Currently assigned to {assignedElsewhere.friendlyName || 'another device'} — will be moved
                              </p>
                            )}
                          </div>
                          {isCurrentlyAssignedHere && (
                            <span className="shrink-0 text-xs font-bold text-green-600">Current</span>
                          )}
                          {assigningSaving === device.id && !isCurrentlyAssignedHere && (
                            <span className="shrink-0 text-xs text-neutral-400">…</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Permanent station */}
            <div>
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                Permanent Station
              </p>
              <div className="flex flex-wrap gap-2">
                {STATION_OPTIONS.map(opt => {
                  const isActive = (device.permanentStation ?? null) === opt.value;
                  return (
                    <button
                      key={String(opt.value)}
                      onClick={() => save(device.id, { permanentStation: opt.value })}
                      disabled={isSaving}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                        isActive
                          ? 'bg-neutral-900 text-white border-neutral-900'
                          : 'bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400 hover:text-neutral-700'
                      }`}
                    >
                      {isSaving && isActive ? '…' : opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-neutral-400 mt-2">
                "None" shows Role Selection on every launch. A fixed station skips that screen.
              </p>
            </div>

            <p className="text-xs text-neutral-300 mt-3 font-mono truncate">{device.id}</p>
          </Card>
        );
      })}
    </div>
  );
};

// ── Subtab 2: Add New Device (TinyMDM enrollment) ────────────────────────────

const ENROLL_STATUS_STYLE = {
  pending:        'bg-amber-100 text-amber-700 border border-amber-200',
  enrolled:       'bg-green-100 text-green-700 border border-green-200',
  decommissioned: 'bg-neutral-100 text-neutral-500 border border-neutral-200',
};

const AddNewDeviceTab = ({ restaurantId }) => {
  const [label,         setLabel]         = useState('');
  const [generating,    setGenerating]    = useState(false);
  const [genError,      setGenError]      = useState('');
  const [enrollment,    setEnrollment]    = useState(null); // result after generation
  const [copied,        setCopied]        = useState(false);
  const [enrollments,   setEnrollments]   = useState([]);
  const [listLoading,   setListLoading]   = useState(true);
  const [listError,     setListError]     = useState('');
  const [deletingId,    setDeletingId]    = useState(null);

  const fetchEnrollments = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const result = await listDeviceEnrollments({ restaurantId });
      setEnrollments(result.data.devices || []);
    } catch (err) {
      setListError(err.message || 'Failed to load enrollments.');
    } finally {
      setListLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);

  const handleGenerate = async () => {
    setGenError('');
    setGenerating(true);
    setEnrollment(null);
    try {
      const result = await createDeviceEnrollment({ restaurantId, deviceLabel: label });
      setEnrollment({ ...result.data, deviceLabel: label || 'Device' });
      setLabel('');
      fetchEnrollments();
    } catch (err) {
      setGenError(err.message || 'Failed to generate enrollment. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(enrollment.enrollmentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDelete = async (deviceId) => {
    setDeletingId(deviceId);
    try {
      await deleteDeviceEnrollmentFn({ restaurantId, deviceId });
      setEnrollments(prev => prev.filter(d => d.deviceId !== deviceId));
      if (enrollment?.deviceId === deviceId) setEnrollment(null);
    } catch (err) {
      console.error('[DevicesView] delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-lg">

      {/* Generate form */}
      <Card>
        <p className="text-sm font-semibold text-neutral-800 mb-1">Generate Enrollment</p>
        <p className="text-sm text-neutral-400 mb-4">
          Creates a TinyMDM enrollment QR code and link for a new Android device.
        </p>
        <label className="block text-sm font-medium text-neutral-700 mb-1.5">
          Device label <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand-blue mb-3"
          placeholder="e.g. Host Stand, Zone 4 Tablet"
          value={label}
          onChange={e => setLabel(e.target.value)}
          disabled={generating}
        />
        {genError && (
          <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{genError}</p>
          </div>
        )}
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="w-full py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {generating ? 'Generating…' : 'Generate Enrollment'}
        </button>
      </Card>

      {/* Enrollment result */}
      {enrollment && (
        <Card>
          <p className="text-sm font-semibold text-neutral-800 mb-1">
            Enrollment ready — {enrollment.deviceLabel}
          </p>
          <p className="text-xs text-neutral-400 mb-4">
            Scan with a factory-reset Android device, or open the link in a browser.
          </p>

          {/* QR code */}
          <div className="flex justify-center mb-4">
            <div className="border-2 border-neutral-200 rounded-xl p-3 bg-white">
              <img src={enrollment.qrCodeDataUrl} alt="Enrollment QR" style={{ width: 200, height: 200 }} />
            </div>
          </div>

          {/* URL */}
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-xs text-neutral-700 break-all font-mono leading-relaxed mb-2">
            {enrollment.enrollmentUrl}
          </div>
          <button
            onClick={handleCopy}
            className="w-full py-2 rounded-lg border border-neutral-200 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>

          <div className="mt-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>Note:</strong> This enrollment link expires after 24 hours. If it expires,
              delete the entry below and generate a new one.
            </p>
          </div>
        </Card>
      )}

      {/* Existing TinyMDM enrollments */}
      <div>
        <p className="text-sm font-semibold text-neutral-700 mb-3">Pending / Enrolled</p>
        {listLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : listError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-sm text-red-600">{listError}</p>
          </div>
        ) : enrollments.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-6">No enrollments yet.</p>
        ) : (
          <div className="space-y-2">
            {enrollments.map(device => (
              <div
                key={device.deviceId}
                className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-800 truncate">
                    {device.deviceLabel || 'Enter Device Name'}
                  </p>
                  <span className={`mt-1 inline-block text-xs font-medium px-2 py-0.5 rounded-full ${ENROLL_STATUS_STYLE[device.status] || ENROLL_STATUS_STYLE.pending}`}>
                    {device.status === 'pending' ? 'Pending enrollment' : device.status === 'enrolled' ? 'Enrolled' : device.status}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(device.deviceId)}
                  disabled={deletingId === device.deviceId}
                  className="shrink-0 py-1.5 px-3 rounded-lg border border-red-200 text-red-500 text-xs font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {deletingId === device.deviceId ? '…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3">
        <p className="text-xs text-neutral-500 leading-relaxed">
          <strong className="text-neutral-700">How it works:</strong> Each enrollment creates a TinyMDM
          user assigned to the GoHost kiosk policy. Once a device scans the QR or opens the link, it
          installs TinyMDM and the GoHost POS app, then locks into kiosk mode automatically.
        </p>
      </div>
    </div>
  );
};

// ── Claim Code card ───────────────────────────────────────────────────────────

const regenerateClaimCode = httpsCallable(getFunctions(), 'regenerateClaimCode');

const ClaimCodeCard = ({ restaurantId }) => {
  const [claimCode,    setClaimCode]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [copied,       setCopied]       = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    getDoc(doc(db, 'restaurants_data', restaurantId)).then(snap => {
      setClaimCode(snap.data()?.claimCode ?? null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [restaurantId]);

  const handleCopy = () => {
    if (!claimCode) return;
    navigator.clipboard.writeText(claimCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await regenerateClaimCode({ restaurantId });
      setClaimCode(res.data?.claimCode ?? claimCode);
    } catch (e) {
      console.error('[ClaimCode] regenerate failed:', e);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="bg-[#5f799a]/10 border border-[#5f799a]/30 rounded-xl px-4 py-3 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[#5f799a] uppercase tracking-wide mb-0.5">
          POS Device Claim Code
        </p>
        {loading ? (
          <p className="text-sm text-neutral-400">Loading…</p>
        ) : claimCode ? (
          <p className="text-xl font-mono font-bold text-neutral-800 tracking-widest">{claimCode}</p>
        ) : (
          <p className="text-sm text-neutral-400 italic">No claim code set — click Regenerate.</p>
        )}
        <p className="text-xs text-neutral-400 mt-0.5">
          Enter this code in the GoHost POS app on a new device to link it to this restaurant.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        {claimCode && (
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#5f799a] text-[#5f799a] hover:bg-[#5f799a]/10 transition-colors"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        )}
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#5f799a] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {regenerating ? '…' : 'Regenerate'}
        </button>
      </div>
    </div>
  );
};

// ── Subtab 3: KDS Settings ────────────────────────────────────────────────────

const RUSH_REPRINT_OPTIONS = [
  {
    value: 'off',
    label: 'Off',
    description: 'No reprint — KDS shows visual and audio alert only.',
  },
  {
    value: 'auto',
    label: 'Auto Reprint',
    description: 'Ticket is automatically re-sent to the printer whenever a server marks an item as rush.',
  },
  {
    value: 'prompt',
    label: 'Prompt Server',
    description: 'Server is asked to confirm before the rush ticket is reprinted.',
  },
];

const KdsSettingsTab = ({ restaurantId }) => {
  const [rushReprintMode, setRushReprintMode] = useState('off');
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    const unsub = onSnapshot(
      doc(db, 'restaurants_data', restaurantId),
      (snap) => {
        if (snap.exists()) {
          const mode = snap.data()?.kdsSettings?.rushReprintMode;
          if (mode) setRushReprintMode(mode);
        }
      },
    );
    return () => unsub();
  }, [restaurantId]);

  const handleSave = async (mode) => {
    setSaving(true);
    setSaved(false);
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        kdsSettings: { rushReprintMode: mode },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('[KdsSettingsTab] save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (mode) => {
    setRushReprintMode(mode);
    handleSave(mode);
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="mb-4">
          <h3 className="text-sm font-bold text-neutral-800">Rush Reprint Mode</h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Controls whether a kitchen ticket is reprinted when a server marks an item as rush from the table screen.
          </p>
        </div>
        <div className="space-y-2">
          {RUSH_REPRINT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleChange(opt.value)}
              disabled={saving}
              className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${
                rushReprintMode === opt.value
                  ? 'border-brand-blue bg-brand-blue/5'
                  : 'border-neutral-200 bg-white hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  rushReprintMode === opt.value
                    ? 'border-brand-blue'
                    : 'border-neutral-300'
                }`}>
                  {rushReprintMode === opt.value && (
                    <div className="w-2 h-2 rounded-full bg-brand-blue" />
                  )}
                </div>
                <div>
                  <div className={`text-sm font-semibold ${
                    rushReprintMode === opt.value ? 'text-brand-blue' : 'text-neutral-700'
                  }`}>{opt.label}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{opt.description}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
        {saved && (
          <p className="text-xs text-emerald-600 font-medium mt-3">Saved</p>
        )}
      </Card>
    </div>
  );
};

// ── DevicesView ───────────────────────────────────────────────────────────────

const SUBTABS = [
  { id: 'devices', label: 'Devices'        },
  { id: 'enroll',  label: 'Add New Device' },
  { id: 'kds',     label: 'KDS Settings'   },
];

const DevicesView = ({ restaurantId }) => {
  const [subTab, setSubTab] = useState('devices');

  return (
    <div className="max-w-2xl">
      <ClaimCodeCard restaurantId={restaurantId} />

      {/* Subtab bar */}
      <div className="flex gap-1 mb-6 border-b border-neutral-200">
        {SUBTABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              subTab === t.id
                ? 'border-brand-blue text-brand-blue'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'devices' && <RegisteredDevicesTab restaurantId={restaurantId} />}
      {subTab === 'enroll'  && <AddNewDeviceTab      restaurantId={restaurantId} />}
      {subTab === 'kds'     && <KdsSettingsTab       restaurantId={restaurantId} />}
    </div>
  );
};

export default DevicesView;
